import {Country} from "../../../../region/types/Country";
import {getCountryId} from "../../../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../../../types/OfferType";
import {offerUrlBuilder} from "../../friendly_offer_list/offer_url_builder";
import {ISlugParsed, offerUrlParser} from "../../friendly_offer_list/offer_url_parser";
import {normalizeListingSearch, serializeCanonicalQuery, serializeListingQuery} from "../canonical_query";
import {normalizeOfferListPath, resolveOfferListPath} from "../compose_offer_list_url";
import {countOfferListFilters, getCategoryHouseType, isMultiFilterQuery, shouldOmitOfferListQueryValue} from "../offer_list_url_filters";
import {IOfferListUrlInput, toOfferListType, toOptionalNumber, toRegionSlug, toValueList} from "../offer_list_url_values";
import {
    IOfferListRouteParams,
    IOfferListRouteQuery,
    IOfferListUrlBuildResult,
    IOfferListUrlParseResult,
    IOfferListUrlStrategy
} from "../types/IOfferListUrlStrategy";
import {buildAbroadOfferListParts, friendlyPathUrlStrategy} from "./friendly_path_url_strategy";

const BASE_KEYS = new Set(["type", "region", "region_type", "country"]);

const omitConsumedKeys = (query: IOfferListUrlInput, consumed: Set<string>): IOfferListUrlInput => {
    const rest: IOfferListUrlInput = {};

    Object.entries(query).forEach(([key, value]) => {
        if (consumed.has(key) || shouldOmitOfferListQueryValue(key, value)) {
            return;
        }

        rest[key] = value;
    });

    return rest;
};

const HOUSE_ONLY_KEYS = new Set(["house_type", "house_storeys", "house_additional_areas"]);

/** House filters apply to house listings only; a flat or mixed listing drops them, as the friendly path builder does. */
const omitHouseFiltersOutsideHouses = (query: IOfferListUrlInput): IOfferListUrlInput =>
    toOfferListType(query.type) === OfferType.HOUSE ? query : omitConsumedKeys(query, HOUSE_ONLY_KEYS);

const buildStaticPath = (query: IOfferListUrlInput, includeCategory: boolean): IOfferListUrlBuildResult => {
    const category = includeCategory ? getCategoryHouseType(query) : null;
    const consumed = new Set(BASE_KEYS);

    if (category != null) {
        consumed.add("house_type");
    }

    const built = offerUrlBuilder({
        type: toOfferListType(query.type),
        region: toRegionSlug(query.region) ?? "",
        ...(category != null ? {house_type: [category]} : {})
    });

    return {
        friendlySlug: built.friendlySlug,
        offerListSubFilterSlug: built.offerListSubFilterSlug || undefined,
        countrySlug: built.countrySlug,
        query: omitConsumedKeys(query, consumed),
        canonicalizeQuery: true
    };
};

const parsedToInput = (parsed: Partial<ISlugParsed>, routeQuery: IOfferListRouteQuery): IOfferListUrlInput => ({
    ...routeQuery,
    ...(parsed.type != null ? {type: parsed.type} : {}),
    ...(parsed.region?.[0] ? {region: parsed.region[0]} : {}),
    ...(parsed.price_0 != null ? {price_0: parsed.price_0} : {}),
    ...(parsed.price_1 != null ? {price_1: parsed.price_1} : {}),
    ...(parsed.area_0 != null ? {area_0: parsed.area_0} : {}),
    ...(parsed.area_1 != null ? {area_1: parsed.area_1} : {}),
    ...(parsed.rooms_0 != null ? {rooms_0: parsed.rooms_0} : {}),
    ...(parsed.rooms_1 != null ? {rooms_1: parsed.rooms_1} : {}),
    ...(parsed.floor_choices != null ? {floor_choices: parsed.floor_choices} : {}),
    ...(parsed.house_type != null ? {house_type: parsed.house_type} : {}),
    ...(parsed.house_storeys != null ? {house_storeys: parsed.house_storeys} : {}),
    ...(parsed.house_additional_areas != null ? {house_additional_areas: parsed.house_additional_areas} : {}),
    ...(parsed.is_luxury ? {is_luxury: true} : {}),
    ...(parsed.price_lower_than_average ? {price_lower_than_average: true} : {}),
    ...(parsed.construction_end_date ? {construction_end_date: parsed.construction_end_date} : {}),
    ...(parsed.country != null ? {country: parsed.country} : {})
});

const routeListingPath = (params: IOfferListRouteParams): string =>
    normalizeOfferListPath(
        resolveOfferListPath({
            friendlySlug: params.friendlySlug || "",
            offerListSubFilterSlug: params.offerListSubFilter,
            countrySlug: params.country,
            abroadRegionSlug: params.abroadRegionSlug,
            isAbroad: Boolean(params.country),
            query: {},
            canonicalizeQuery: true
        })
    );

const currentPath = (params: IOfferListRouteParams): string => (params.pathname ? normalizeOfferListPath(params.pathname) : routeListingPath(params));

/** Map segments after the listing path (`mapa/`, `mapa/kredyt-2-procent/`). */
const currentMapSuffix = (params: IOfferListRouteParams): string => {
    const listingPath = routeListingPath(params);
    const requestPath = currentPath(params);
    const suffix = requestPath.startsWith(listingPath) ? requestPath.slice(listingPath.length) : "";

    return suffix.startsWith("mapa/") ? suffix : "";
};

const abroadRouteInput = (params: IOfferListRouteParams): IOfferListUrlInput => {
    if (!params.country) {
        return {};
    }

    return {
        country: getCountryId(params.country),
        ...(params.abroadRegionSlug ? {region: params.abroadRegionSlug} : {})
    };
};

/**
 * 0 filters → static category URL (house subtype stays a path segment).
 * 1 filter → the single-filter friendly slug.
 * 2+ filters, or a subtype plus another filter → base slug `{nowe}-{type}-{region}` and filters in the query.
 * A static path that already encodes a multifilter is gone (410). A still-valid path with extra query filters
 * redirects to the canonical query URL. Query-only house subtypes are not promoted back into the path,
 * so the existing non-city subfilter redirect cannot loop.
 */
export const hybridQueryParamsUrlStrategy: IOfferListUrlStrategy = {
    canonicalizeQuery: true,
    build(rawQuery) {
        const query = omitHouseFiltersOutsideHouses(rawQuery);
        const country = toOptionalNumber(query.country);

        if (country && country !== Country.POLAND) {
            return buildAbroadOfferListParts(query, true);
        }

        if (!isMultiFilterQuery(query) && countOfferListFilters(query) === 1) {
            return {...friendlyPathUrlStrategy.build(query), canonicalizeQuery: true};
        }

        if (!isMultiFilterQuery(query)) {
            return buildStaticPath(query, true);
        }

        return buildStaticPath(query, false);
    },
    parse(params, routeQuery): IOfferListUrlParseResult {
        if (!params.friendlySlug) {
            return {status: "invalid"};
        }

        const parsed = offerUrlParser(params.friendlySlug, params.offerListSubFilter);

        if (!parsed) {
            return {status: "invalid"};
        }

        const pathQuery = parsedToInput(parsed, {});

        if (isMultiFilterQuery(pathQuery)) {
            return {status: "gone"};
        }

        const merged = {...parsedToInput(parsed, routeQuery), ...abroadRouteInput(params)};
        const requestPath = currentPath(params);
        // Only the listing's own keys decide on a redirect; utm_* and other parameters ride along unchanged.
        const requestSearch = params.search === undefined ? undefined : normalizeListingSearch(params.search);
        const isRegionOnlyInQuery = !parsed.region?.length && !params.abroadRegionSlug && toValueList(routeQuery.region).length > 0;

        if (!isRegionOnlyInQuery && isMultiFilterQuery(merged)) {
            const mapSuffix = currentMapSuffix(params);
            const listingBuilt = this.build(merged);
            // Map routes have no subfilter segment, so a filter that would become one stays in the query there.
            const built = mapSuffix && listingBuilt.offerListSubFilterSlug ? buildStaticPath(omitHouseFiltersOutsideHouses(merged), false) : listingBuilt;
            const canonicalPath = `${normalizeOfferListPath(resolveOfferListPath(built))}${mapSuffix}`;
            const canonicalSearch = serializeCanonicalQuery(built.query);
            const canonicalUrl = canonicalSearch ? `${canonicalPath}?${canonicalSearch}` : canonicalPath;
            const pathChanged = canonicalPath !== requestPath;
            const searchChanged = requestSearch !== undefined && requestSearch !== serializeListingQuery(built.query);

            if (pathChanged || searchChanged) {
                return {status: "ok", parsed, canonicalUrl};
            }

            return {status: "ok", parsed};
        }

        if (requestSearch !== undefined && requestSearch !== serializeListingQuery(routeQuery)) {
            const canonicalSearch = serializeCanonicalQuery(routeQuery);
            const canonicalUrl = canonicalSearch ? `${requestPath}?${canonicalSearch}` : requestPath;

            return {status: "ok", parsed, canonicalUrl};
        }

        return {status: "ok", parsed};
    },
    isIndexable(query) {
        return !isMultiFilterQuery(query);
    }
};
import {Country} from "../../../../region/types/Country";
import {getCountryId} from "../../../list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../../../types/OfferType";
import {offerUrlBuilder} from "../../friendly_offer_list/offer_url_builder";
import {ISlugParsed, offerUrlParser} from "../../friendly_offer_list/offer_url_parser";
import {normalizeListingSearch, serializeCanonicalQuery, serializeListingQuery} from "../canonical_query";
import {normalizeOfferListPath, resolveOfferListPath} from "../compose_offer_list_url";
import {countOfferListFilters, getCategoryHouseType, isMultiFilterQuery, shouldOmitOfferListQueryValue} from "../offer_list_url_filters";
import {IOfferListUrlInput, toOfferListType, toOptionalNumber, toRegionSlug, toValueList} from "../offer_list_url_values";
import {
    IOfferListRouteParams,
    IOfferListRouteQuery,
    IOfferListUrlBuildResult,
    IOfferListUrlParseResult,
    IOfferListUrlStrategy
} from "../types/IOfferListUrlStrategy";
import {buildAbroadOfferListParts, friendlyPathUrlStrategy} from "./friendly_path_url_strategy";

const BASE_KEYS = new Set(["type", "region", "region_type", "country"]);

const omitConsumedKeys = (query: IOfferListUrlInput, consumed: Set<string>): IOfferListUrlInput => {
    const rest: IOfferListUrlInput = {};

    Object.entries(query).forEach(([key, value]) => {
        if (consumed.has(key) || shouldOmitOfferListQueryValue(key, value)) {
            return;
        }

        rest[key] = value;
    });

    return rest;
};

const HOUSE_ONLY_KEYS = new Set(["house_type", "house_storeys", "house_additional_areas"]);

/** House filters apply to house listings only; a flat or mixed listing drops them, as the friendly path builder does. */
const omitHouseFiltersOutsideHouses = (query: IOfferListUrlInput): IOfferListUrlInput =>
    toOfferListType(query.type) === OfferType.HOUSE ? query : omitConsumedKeys(query, HOUSE_ONLY_KEYS);

const buildStaticPath = (query: IOfferListUrlInput, includeCategory: boolean): IOfferListUrlBuildResult => {
    const category = includeCategory ? getCategoryHouseType(query) : null;
    const consumed = new Set(BASE_KEYS);

    if (category != null) {
        consumed.add("house_type");
    }

    const built = offerUrlBuilder({
        type: toOfferListType(query.type),
        region: toRegionSlug(query.region) ?? "",
        ...(category != null ? {house_type: [category]} : {})
    });

    return {
        friendlySlug: built.friendlySlug,
        offerListSubFilterSlug: built.offerListSubFilterSlug || undefined,
        countrySlug: built.countrySlug,
        query: omitConsumedKeys(query, consumed),
        canonicalizeQuery: true
    };
};

const parsedToInput = (parsed: Partial<ISlugParsed>, routeQuery: IOfferListRouteQuery): IOfferListUrlInput => ({
    ...routeQuery,
    ...(parsed.type != null ? {type: parsed.type} : {}),
    ...(parsed.region?.[0] ? {region: parsed.region[0]} : {}),
    ...(parsed.price_0 != null ? {price_0: parsed.price_0} : {}),
    ...(parsed.price_1 != null ? {price_1: parsed.price_1} : {}),
    ...(parsed.area_0 != null ? {area_0: parsed.area_0} : {}),
    ...(parsed.area_1 != null ? {area_1: parsed.area_1} : {}),
    ...(parsed.rooms_0 != null ? {rooms_0: parsed.rooms_0} : {}),
    ...(parsed.rooms_1 != null ? {rooms_1: parsed.rooms_1} : {}),
    ...(parsed.floor_choices != null ? {floor_choices: parsed.floor_choices} : {}),
    ...(parsed.house_type != null ? {house_type: parsed.house_type} : {}),
    ...(parsed.house_storeys != null ? {house_storeys: parsed.house_storeys} : {}),
    ...(parsed.house_additional_areas != null ? {house_additional_areas: parsed.house_additional_areas} : {}),
    ...(parsed.is_luxury ? {is_luxury: true} : {}),
    ...(parsed.price_lower_than_average ? {price_lower_than_average: true} : {}),
    ...(parsed.construction_end_date ? {construction_end_date: parsed.construction_end_date} : {}),
    ...(parsed.country != null ? {country: parsed.country} : {})
});

const routeListingPath = (params: IOfferListRouteParams): string =>
    normalizeOfferListPath(
        resolveOfferListPath({
            friendlySlug: params.friendlySlug || "",
            offerListSubFilterSlug: params.offerListSubFilter,
            countrySlug: params.country,
            abroadRegionSlug: params.abroadRegionSlug,
            isAbroad: Boolean(params.country),
            query: {},
            canonicalizeQuery: true
        })
    );

const currentPath = (params: IOfferListRouteParams): string => (params.pathname ? normalizeOfferListPath(params.pathname) : routeListingPath(params));

/** Map segments after the listing path (`mapa/`, `mapa/kredyt-2-procent/`). */
const currentMapSuffix = (params: IOfferListRouteParams): string => {
    const listingPath = routeListingPath(params);
    const requestPath = currentPath(params);
    const suffix = requestPath.startsWith(listingPath) ? requestPath.slice(listingPath.length) : "";

    return suffix.startsWith("mapa/") ? suffix : "";
};

const abroadRouteInput = (params: IOfferListRouteParams): IOfferListUrlInput => {
    if (!params.country) {
        return {};
    }

    return {
        country: getCountryId(params.country),
        ...(params.abroadRegionSlug ? {region: params.abroadRegionSlug} : {})
    };
};

/**
 * 0 filters → static category URL (house subtype stays a path segment).
 * 1 filter → the single-filter friendly slug.
 * 2+ filters, or a subtype plus another filter → base slug `{nowe}-{type}-{region}` and filters in the query.
 * A static path that already encodes a multifilter is gone (410). A still-valid path with extra query filters
 * redirects to the canonical query URL. Query-only house subtypes are not promoted back into the path,
 * so the existing non-city subfilter redirect cannot loop.
 */
export const hybridQueryParamsUrlStrategy: IOfferListUrlStrategy = {
    canonicalizeQuery: true,
    build(rawQuery) {
        const query = omitHouseFiltersOutsideHouses(rawQuery);
        const country = toOptionalNumber(query.country);

        if (country && country !== Country.POLAND) {
            return buildAbroadOfferListParts(query, true);
        }

        if (!isMultiFilterQuery(query) && countOfferListFilters(query) === 1) {
            return {...friendlyPathUrlStrategy.build(query), canonicalizeQuery: true};
        }

        if (!isMultiFilterQuery(query)) {
            return buildStaticPath(query, true);
        }

        return buildStaticPath(query, false);
    },
    parse(params, routeQuery): IOfferListUrlParseResult {
        if (!params.friendlySlug) {
            return {status: "invalid"};
        }

        const parsed = offerUrlParser(params.friendlySlug, params.offerListSubFilter);

        if (!parsed) {
            return {status: "invalid"};
        }

        const pathQuery = parsedToInput(parsed, {});

        if (isMultiFilterQuery(pathQuery)) {
            return {status: "gone"};
        }

        const merged = {...parsedToInput(parsed, routeQuery), ...abroadRouteInput(params)};
        const requestPath = currentPath(params);
        // Only the listing's own keys decide on a redirect; utm_* and other parameters ride along unchanged.
        const requestSearch = params.search === undefined ? undefined : normalizeListingSearch(params.search);
        const isRegionOnlyInQuery = !parsed.region?.length && !params.abroadRegionSlug && toValueList(routeQuery.region).length > 0;

        if (!isRegionOnlyInQuery && isMultiFilterQuery(merged)) {
            const mapSuffix = currentMapSuffix(params);
            const listingBuilt = this.build(merged);
            // Map routes have no subfilter segment, so a filter that would become one stays in the query there.
            const built = mapSuffix && listingBuilt.offerListSubFilterSlug ? buildStaticPath(omitHouseFiltersOutsideHouses(merged), false) : listingBuilt;
            const canonicalPath = `${normalizeOfferListPath(resolveOfferListPath(built))}${mapSuffix}`;
            const canonicalSearch = serializeCanonicalQuery(built.query);
            const canonicalUrl = canonicalSearch ? `${canonicalPath}?${canonicalSearch}` : canonicalPath;
            const pathChanged = canonicalPath !== requestPath;
            const searchChanged = requestSearch !== undefined && requestSearch !== serializeListingQuery(built.query);

            if (pathChanged || searchChanged) {
                return {status: "ok", parsed, canonicalUrl};
            }

            return {status: "ok", parsed};
        }

        if (requestSearch !== undefined && requestSearch !== serializeListingQuery(routeQuery)) {
            const canonicalSearch = serializeCanonicalQuery(routeQuery);
            const canonicalUrl = canonicalSearch ? `${requestPath}?${canonicalSearch}` : requestPath;

            return {status: "ok", parsed, canonicalUrl};
        }

        return {status: "ok", parsed};
    },
    isIndexable(query) {
        return !isMultiFilterQuery(query);
    }
};
